"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.updateSingletonSdkConfig = exports.initializeIssuesReduxStore = exports.getStoreInitializationTime = exports.getSingletonIssuesReduxStore = exports.getOrInitializeStore = exports.createStore = void 0;
var _toolkit = require("@reduxjs/toolkit");
var _sdk = require("../../../sdk");
var _rootReducer = require("./rootReducer");
var _currentUserPermissions = require("./ducks/currentUserPermissions");
var _issueCustomFields = require("./ducks/issueCustomFields");
var _issueCustomFieldMappings = require("./ducks/issueCustomFieldMappings");
var _issueTypes = require("./ducks/issueTypes");
var _issueRootCauses = require("./ducks/issueRootCauses");
var _issueTemplates = require("./ducks/issueTemplates");
const createStore = _ref => {
  let {
    sdk
  } = _ref;
  return (0, _toolkit.configureStore)({
    reducer: _rootReducer.rootReducer,
    middleware: (0, _toolkit.getDefaultMiddleware)({
      thunk: {
        extraArgument: {
          sdk
        }
      },
      immutableCheck: false,
      serializableCheck: false
    })
  });
};
exports.createStore = createStore;
/**
 * Using a singleton is necessary for some contexts, such as the sheet viewer.
 * This is because when used as a plugin, we can't necessarily add an `IssueProvider` as the parent component
 * of all the components that will be under our control, so we need to be able to wrap one individually.
 * So the state variable itself can't be managed by the IssueProvider component -- it will need to access
 * thse shared singletons.
 *
 */
let singletonProjectId;
let singletonIssuesSdk;
let singletonIssuesReduxStore;
let storeInitializedAt;
const getOrInitializeStore = _ref2 => {
  let {
    projectId,
    headers = {},
    shouldInitializeStore = true
  } = _ref2;
  if (!projectId) {
    throw new Error("projectId cannot be null when calling getOrInitializeStore");
  }
  if (singletonProjectId && projectId !== singletonProjectId) {
    throw new Error("only one project ID is supported for the issue store singleton at this time");
  }
  if (!singletonProjectId || !singletonIssuesSdk || !singletonIssuesReduxStore) {
    singletonIssuesSdk = (0, _sdk.createSDK)({
      projectId,
      headers
    });
    singletonIssuesReduxStore = createStore({
      sdk: singletonIssuesSdk
    });
    singletonProjectId = projectId;
    if (shouldInitializeStore) {
      initializeIssuesReduxStore();
    }
  }
  return {
    sdk: singletonIssuesSdk,
    store: singletonIssuesReduxStore
  };
};

/**
 * This is needed by the sheet viewer, which uses the store without being part of
 * the IssueProvider component context.
 *
 * A `getSingletonIssuesSDK` function does not exist yet,
 * because such a thing is not yet used by any consumer
 */
exports.getOrInitializeStore = getOrInitializeStore;
const getSingletonIssuesReduxStore = () => {
  if (singletonIssuesReduxStore === null) {
    throw new Error("redux store must be initialized before it can be accessed");
  }
  return singletonIssuesReduxStore;
};

/**
 * Updates the configuration of the singleton issues sdk object.
 * The function swaps the sdk configurations while keeping the original sdk object.
 */
exports.getSingletonIssuesReduxStore = getSingletonIssuesReduxStore;
const updateSingletonSdkConfig = config => {
  if (singletonIssuesSdk) {
    (0, _sdk.updateSDK)(singletonIssuesSdk, config);
  }
};

/**
 * Trigger requests to initialize the redux store.
 */
exports.updateSingletonSdkConfig = updateSingletonSdkConfig;
const initializeIssuesReduxStore = () => {
  singletonIssuesReduxStore.dispatch((0, _issueTypes.fetchIssueTypesAction)());
  singletonIssuesReduxStore.dispatch((0, _issueRootCauses.fetchIssueRootCausesAction)());
  singletonIssuesReduxStore.dispatch((0, _currentUserPermissions.fetchCurrentUserPermissionsAction)());
  singletonIssuesReduxStore.dispatch((0, _issueCustomFields.fetchIssueCustomFieldsAction)());
  singletonIssuesReduxStore.dispatch((0, _issueCustomFieldMappings.fetchIssueCustomFieldMappingsAction)());
  // it is OK to run the `fetchIssueTemplatesAction` even if the FF for templates is off...
  // accessing FFs here is tricky, since it is potentially called from outside our LD context,
  // and even in our LD context it will only be called once, potentially _before_ the FFs load.
  // It will be safe to run this action even with the FF off though, so we do that.
  singletonIssuesReduxStore.dispatch((0, _issueTemplates.fetchIssueTemplatesAction)());
  // The main use case of `initializeIssuesReduxStore` is to be able to create the store and the sdk
  // objects and run the store initialization process after the FFs are loaded. Knowing the last
  // time when the store was initialized helps us to avoid running this function unnecessarily.
  storeInitializedAt = Date.now();
};

/**
 * The IssueProvider delays the store initialization process until the feature flags are loaded.
 * IssueProvider uses this function to prevent calling `initializeIssuesReduxStore` multiple times.
 */
exports.initializeIssuesReduxStore = initializeIssuesReduxStore;
const getStoreInitializationTime = () => {
  return storeInitializedAt;
};
exports.getStoreInitializationTime = getStoreInitializationTime;
//# sourceMappingURL=store.js.map